import { useEffect, type ReactElement } from 'react';
import { Box, Group, Loader, Text } from '@mantine/core';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useMegaGridState } from '@/design/MegaGrid/MegaGrid';
import EvidenceTable from '@/shared/surfaces/EvidenceTable';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { formatCount } from '@/shared/analytics/MetricFormatting';
import { UseCasePromptsViewModel } from './UseCasePromptsViewModel';
import { USE_CASE_PROMPT_COLUMNS } from './UseCasePromptColumns';

const PROMPT_PAGE_SIZE = 25;

interface UseCasePromptsTabProps {
    /** Server-side scope (slice + ambient); memoize at the call site. */
    filters: AnalyticsFilter[];
}

/** The Prompts tab body: the slice's prompt-level evidence grid, shared by the category and subcategory pages. */
export default function UseCasePromptsTab({ filters }: UseCasePromptsTabProps): ReactElement {
    const vm = useViewModel(UseCasePromptsViewModel);
    const controller = useMegaGridState({
        columns: USE_CASE_PROMPT_COLUMNS,
        persistenceKey: 'use-case-prompts',
        defaultDensity: 'comfortable',
        defaultGroupBy: [],
        allowMultiSort: true,
    });

    useEffect(() => {
        void vm.load(filters);
    }, [vm, filters]);

    if (vm.errorMessage !== null) {
        return (
            <Text size="sm" c="red">
                {vm.errorMessage}
            </Text>
        );
    }
    if (vm.loading && vm.records.length === 0) {
        return (
            <Group justify="center" gap="sm" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Loading prompts…
                </Text>
            </Group>
        );
    }

    return (
        <Box>
            <EvidenceTable records={vm.records} accent="teal" columns={USE_CASE_PROMPT_COLUMNS} clientPageSize={PROMPT_PAGE_SIZE} controller={controller} persistenceKey="use-case-prompts" />
            {vm.totalMatched > vm.records.length && (
                <Text size="xs" c="dimmed" mt="sm">
                    Showing the {formatCount(vm.records.length)} most recent of {formatCount(vm.totalMatched)} matching prompts.
                </Text>
            )}
        </Box>
    );
}
